/**
 * Time - Singleton for server-synced time and date utilities
 *
 * SINGLETON: Provides time/date utilities with server synchronization to ensure
 * accurate timestamps across client and server. Wraps Moment.js and Moment Timezone.
 *
 * PUBLIC API NOTE: All public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * KEY FEATURE: Server-Synced Time
 * - getTime() returns server-synchronized timestamp (not Date.now())
 * - Accounts for client/server time drift
 * - Critical for time-sensitive operations (countdowns, sales windows)
 *
 * FEATURES:
 * - Server-synced timestamps
 * - Timezone conversion and formatting
 * - Relative time (e.g., "2 hours ago")
 * - Date parsing and manipulation
 * - Custom format strings
 * - Timezone detection (jstz)
 * - Moment.js integration
 *
 * @class Time
 * @extends Class
 * @singleton
 *
 * @example
 * // Get server-synced timestamp (use this instead of Date.now())
 * const now = Time.getTime();
 *
 * @example
 * // Format date with timezone
 * const formatted = Time.format('%B %d, %Y', timestamp, 'America/Los_Angeles');
 *
 * @see Moment.js documentation for format strings and manipulation
 */
define("Time", [`Class`, `module`, `moment_timezone`, `jstz`], function (Super, Self, moment, jstz) {
	var date = new Date(),
		LOCAL_STARTUP = date.getTime(),
		PREFERRED_TIMEZONE,
		SERVER_STARTUP,
		ZONE_ABBR_MAP = {
			'-12': `UTC -12`,
			'-11': `UTC -11`,
			'-10': `UTC -10`,
			'-0930': `UTC -09:30`,
			'-09': `UTC -09`,
			'-08': `UTC -08`,
			'-07': `UTC -07`,
			'-06': `UTC -06`,
			'-05': `UTC -05`,
			'-04': `UTC -04`,
			'-0330': `UTC -03:30`,
			'-03': `UTC -03`,
			'-02': `UTC -02`,
			'-01': `UTC -01`,
			'±00': `UTC ±00`,
			'+01': `UTC +01`,
			'+02': `UTC +02`,
			'+03': `UTC +03`,
			'+0330': `UTC +03:30`,
			'+04': `UTC +04`,
			'+0430': `UTC +04:30`,
			'+05': `UTC +05`,
			'+0530': `UTC +05:30`,
			'+0545': `UTC +05:45`,
			'+06': `UTC +06`,
			'+0630': `UTC +06:30`,
			'+07': `UTC +07`,
			'+08': `UTC +08`,
			'+0845': `UTC +08:45`,
			'+09': `UTC +09`,
			'+0930': `UTC +09:30`,
			'+10': `UTC +10`,
			'+1030': `UTC +10:30`,
			'+11': `UTC +11`,
			'+12': `UTC +12`,
			'+1245': `UTC +12:45`,
			'+13': `UTC +13`,
			'+14': `UTC +14`,
		};
	var ZONE_ABBR = function () {
		var abbr = this.zoneAbbr();
		return ZONE_ABBR_MAP[abbr] || abbr;
	};
	const MS_PER_MINUTE = 60000;
	const zoneNameLabel = function (timeZoneName, timestamp, timezone, locale) {
		if (timeZoneName === `short`) {
			const local = moment(timestamp).tz(timezone);

			if (new Intl.DateTimeFormat(locale).resolvedOptions().locale.startsWith(`en`)) {
				return ZONE_ABBR.call(local);
			}

			const offset = local.utcOffset();

			if (!offset) {
				return `UTC`;
			}

			const absolute = Math.abs(offset);
			const minutes = absolute % 60;
			return `UTC${offset < 0 ? `−` : `+`}${Math.floor(absolute / 60)}${
				minutes ? `:${String(minutes).padStart(2, `0`)}` : ``
			}`;
		}

		if (timeZoneName === `longOffset`) {
			const local = moment(timestamp).tz(timezone);
			return local.utcOffset() ? local.format(`[GMT]Z`) : `GMT`;
		}

		const part = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName })
			.formatToParts(new Date(timestamp))
			.find(function (p) {
				return p.type === `timeZoneName`;
			});

		return part ? part.value : ``;
	};
	const formatInZone = function (locale, localeOptions, timestamp, timezone, endTimestamp) {
		const zone = moment.tz.zone(timezone);
		const isRange = endTimestamp !== undefined;

		if (!zone) {
			const formatter = new Intl.DateTimeFormat(locale, Object.assign({}, localeOptions, { timeZone: timezone }));
			return isRange
				? formatter.formatRange(new Date(timestamp), new Date(endTimestamp))
				: formatter.format(new Date(timestamp));
		}

		const shift = function (value) {
			return new Date(value - zone.utcOffset(value) * MS_PER_MINUTE);
		};
		const formatter = new Intl.DateTimeFormat(locale, Object.assign({}, localeOptions, { timeZone: `UTC` }));
		const parts = isRange
			? formatter.formatRangeToParts(shift(timestamp), shift(endTimestamp))
			: formatter.formatToParts(shift(timestamp));
		const labelTimestamp = isRange ? endTimestamp : timestamp;

		return parts
			.map(function (part) {
				const partTimestamp = part.source === `startRange` ? timestamp : labelTimestamp;
				return part.type === `timeZoneName`
					? zoneNameLabel(localeOptions.timeZoneName, partTimestamp, timezone, locale)
					: part.value;
			})
			.join(``);
	};
	// Defines template replacements to convert a template to locale options
	// NOTE: If this is updated, also update replacements in Time#format
	const LOCALE_REPLACEMENTS = [
		{ key: `%Y`, localeOptions: { year: `numeric` } }, // Four-digit year
		{ key: `%y`, localeOptions: { year: `2-digit` } }, // Two-digit year
		{ key: `%N`, localeOptions: { month: `2-digit` } }, // Two-digit month, enforce leading zero
		{ key: `%n`, localeOptions: { month: `numeric` } }, // Month number
		{ key: `%M`, localeOptions: { month: `long` } }, // Full month name
		{ key: `%m`, localeOptions: { month: `short` } }, // Abbreviated month name
		{ key: `%D`, localeOptions: { day: `2-digit` } }, // Date day (e.g. May *21*), enforce leading zero
		{ key: `%d`, localeOptions: { day: `numeric` } }, // Date day (e.g. May *21*)
		// { key: `%L`, localeOptions: { } }, // Either a relevant day label, like Today, or the day of the week
		{ key: `%W`, localeOptions: { weekday: `long` } }, // Full weekday name
		{ key: `%w`, localeOptions: { weekday: `short` } }, // Abbreviated weekday name
		{ key: `%J`, localeOptions: { hour12: false } }, // 24-hour hour (enforce leading zeroes)
		{ key: `%H`, localeOptions: { hour: `2-digit` } }, // two-digit hour (enforce leading zeroes)
		{ key: `%h`, localeOptions: { hour: `numeric` } }, // hour
		{ key: `%i`, localeOptions: { minute: `2-digit` } }, // two-digit minute (enforce leading zero)
		{ key: `%S`, localeOptions: { year: `2-digit` } }, // second
		{ key: `%A`, localeOptions: {} }, // AM/PM
		{ key: `%a`, localeOptions: {} }, // am/pm
		{ key: `%T`, localeOptions: { timeZoneName: `long` } }, // full timezone
		{ key: `%t`, localeOptions: { timeZoneName: `short` } }, // short timezone
		{ key: `%Z`, localeOptions: { timeZoneName: `longOffset` } }, // timezone offset
	];
	const DATE_TIME_FORMATS = {
		DATE: {
			template: `%m %d, %Y`,
		},
		DATE_LONG: {
			template: `%w %m %d, %Y`,
		},
		DATE_MEDIUM: {
			template: `%w %m %d`,
		},
		DATE_SHORT: {
			template: `%m %d`,
		},
		TIME: {
			template: `%H:%i%a %t`,
		},
		TIME_SHORT: {
			template: `%H:%i%a`,
		},
		DATE_TIME: {
			template: `%m %d, %Y %H:%i%a %t`,
		},
		DATE_TIME_FULL: {
			// use full formats with caution. Switching from a FULL to a non-FULL (and vice-versa) will may require re-translation in LocalizeJS
			template: `%m %d, %Y at %H:%i%a %t`,
		},
		DATE_LONG_TIME_FULL: {
			// use full formats with caution. Switching from a FULL to a non-FULL (and vice-versa) will may require re-translation in LocalizeJS
			template: `%w %m %d, %Y at %H:%i%a %t`,
		},
		DATE_MEDIUM_TIME_FULL: {
			// use full formats with caution. Switching from a FULL to a non-FULL (and vice-versa) will may require re-translation in LocalizeJS
			template: `%w %m %d at %H:%i%a %t`,
		},
		DATE_SHORT_TIME_FULL: {
			// use full formats with caution. Switching from a FULL to a non-FULL (and vice-versa) will may require re-translation in LocalizeJS
			template: `%m %d at %h %a %t`,
		},
		DATE_X_SHORT: {
			// avoid using this format, it's ambiguous
			template: `%n/%d/%Y`,
		},
		DATE_TIME_X_SHORT: {
			// avoid using this format, it's ambiguous
			template: `%n/%d/%Y %H:%i%a`,
		},
		WEEKDAY_LONG: {
			template: `%W`,
		},
		WEEKDAY_SHORT: {
			template: `%w`,
		},
		WEEKDAY_X_SHORT: {
			template: `%w`,
		},
	};
	let SUPPORTS_LOCALE = false;

	return Super.inherit({
		namespace: Self.id,
		requires: [`XHR`],
		singleton: true,
		constructor(setup) {
			moment.fn.zoneName = ZONE_ABBR;

			SUPPORTS_LOCALE = this.checkSupportsLocale();

			// if the browser supports the Intl API, pre set the locale options for each format so they don't need to be computed each time
			if (SUPPORTS_LOCALE) {
				this.presetLocaleOptions();
			}

			this.XHR.getServerTime({
				then: function (timestamp) {
					// Fetch server time when it's available from an xhr request header
					SERVER_STARTUP = timestamp;
				},
				done: true,
			});
		},

		api: {
			/**
			 * Checks to see if the browser supports the Intl API
			 * @method Time#checkSupportsLocale
			 * @returns {boolean} True if the browser supports the Intl API
			 */
			checkSupportsLocale: {
				value() {
					const { Intl } = window;

					return typeof Intl === `object` && !!Intl && typeof Intl.DateTimeFormat === `function`;
				},
			},

			/**
			 * Presets the locale options for each format so they don't need to be computed each time
			 * @method Time#presetLocaleOptions
			 */
			presetLocaleOptions: {
				value() {
					this.each(DATE_TIME_FORMATS, (format, key) => {
						if (format.localeOptions) {
							return; // already predefined
						}

						format.localeOptions = this.templateToLocaleOptions(format.template);
					});
				},
			},

			/**
			 * Get current server-synced timestamp (use instead of Date.now()).
			 *
			 * @method Time#getTime
			 * @param {Object} [options] - Time options
			 * @param {boolean} [options.round=false] - Round to nearest 15 minutes
			 * @returns {number} Server-synced Unix timestamp (ms)
			 */
			getTime: {
				public: true,
				defaults: {
					round: false,
				},
				value(context, options) {
					var date = new Date(),
						value = date.getTime(), // get the system time
						minutes; // potentially used for rounding

					if (this.isInt(SERVER_STARTUP)) {
						// we have server time
						value = SERVER_STARTUP + (value - LOCAL_STARTUP); // use server time + local elapsed
					}

					if (options.round) {
						// round to nearest 15 minutes (used for pre-populating inputs, etc.)
						date = new Date(value);
						minutes = date.getMinutes();

						// round to nearest 15 minutes
						minutes = minutes >= 45 ? 45 : minutes >= 30 ? 30 : minutes >= 15 ? 15 : 0;
						date.setMinutes(minutes);
						date.setSeconds(0);
						date.setMilliseconds(0);
						value = date.getTime();
					}

					return value;
				},
			},

			/**
			 * Get detailed timezone info for timestamp.
			 *
			 * @method Time#getInfo
			 * @param {number} [timestamp] - Unix timestamp (defaults to now)
			 * @param {string} [timezone] - Timezone name (defaults to preferred)
			 * @returns {Object} Timezone details (offset, abbreviation, UTC, etc.)
			 */
			getInfo: {
				public: true,
				value(context, timestamp, timezone) {
					if (!this.isInt(timestamp)) {
						timestamp = this.getTime();
					}

					if (!this.isString(timezone, 1)) {
						timezone = this.getTimezone();
					}

					var m = moment(timestamp).tz(timezone),
						timezoneOffset = m.utcOffset() * 60000,
						localOffset = -1 * new Date().getTimezoneOffset() * 60000;

					return {
						localTimezone: this.getTimezone(),
						localOffset,
						timezone: m.tz(),
						timezoneShort: m.zoneAbbr(),
						timezoneOffset, // 1000 ms * 60 sec, to get offset in ms
						utc: timestamp,
						localUtc: timestamp + (timezoneOffset - localOffset), // new Date(localUtc) should return a date simulating the UTC time in the local timezone
					};
				},
			},

			/**
			 * Get ISO 8601 datetime string.
			 *
			 * @method Time#getDatetime
			 * @param {Object} [options] - Options
			 * @param {number} [options.timestamp] - Unix timestamp (defaults to now)
			 * @returns {string} ISO datetime string (YYYY-MM-DDTHH:mmZ)
			 */
			getDatetime: {
				public: true,
				value(context, options) {
					const template = `%Y-%N-%DT%J:%iZ`;
					let { timestamp } = options;

					if (timestamp === undefined) {
						// no timestamp passed, default to current time
						timestamp = this.getTime();
					}

					return this.format(template, timestamp, `UTC`);
				},
			},

			/**
			 * Get number of days in a month.
			 *
			 * @method Time#getDaysInMonth
			 * @param {number} year - Year
			 * @param {number} month - Month (0-indexed: 0 = January)
			 * @returns {number} Number of days in month
			 */
			getDaysInMonth: {
				public: true,
				value(context, year, month) {
					var date = new Date(year, month);
					date.setDate(32);
					return 32 - date.getDate();
				},
			},

			/**
			 * Get day of year (1-366).
			 *
			 * @method Time#getDayOfYear
			 * @param {number} [timestamp] - Unix timestamp (defaults to now)
			 * @param {string} [timezone] - Timezone name (defaults to preferred)
			 * @returns {number} Day of year (1-366)
			 */
			getDayOfYear: {
				public: true,
				value(context, timestamp, timezone) {
					if (!this.isInt(timestamp)) {
						timestamp = this.getTime();
					}

					if (!this.isString(timezone, 1)) {
						timezone = this.getTimezone();
					}

					var dayOfYear = moment(timestamp).tz(timezone).format(`DDD`);
					return parseInt(dayOfYear, 10);
				},
			},

			/**
			 * Generate calendar grid for a month (array of week arrays).
			 *
			 * @method Time#getCalendarGrid
			 * @param {Object} options - Calendar options
			 * @param {number} options.month - Month (1-12)
			 * @param {number} options.year - Year
			 * @param {boolean} [options.insertAdjacentValues=false] - Fill with adjacent month days
			 * @param {boolean} [options.insertNullValues=true] - Fill with null values
			 * @returns {Array[]} Array of week arrays
			 */
			getCalendarGrid: {
				// generates an array of arrays, representing a grid of dates, based on a passed month / year
				public: true,
				defaults: {
					insertAdjacentValues: false, // <Boolean> Fill out every array, using adjacent month's days, e.g. [ [30, 31, 1, 2, 3, 4, 5], [6, 7, ...], ... ]
					insertNullValues: true, // <Boolean> Fill every array, using null values, e.g. [ [null, null, 1, 2, 3, 4, 5], [6, 7, ...], ... ]
					month: null, // <Integer> 1-12, representing the numerical value of the month
					year: null, // <Integer>
				},
				value(context, options) {
					var daysThisMonth,
						daysLastMonth,
						previousMonth,
						previousYear,
						currentMonth,
						currentYear,
						nextMonth,
						firstDayIndex,
						dummyDate,
						result,
						range,
						days,
						day;

					if (!this.isInt(options.month, 1) || options.month > 12) {
						throw new Error(`Time.getCalendarGrid(): Invalid 'month' param`);
					}

					if (!this.isInt(options.year)) {
						throw new Error(`Time.getCalendarGrid(): Invalid 'year' param`);
					}

					result = [];

					currentYear = options.year;
					currentMonth = options.month;

					if (currentMonth === 1) {
						previousYear = currentYear - 1;
						previousMonth = 12;
						nextMonth = 2;
					} else if (currentMonth === 12) {
						previousYear = currentYear;
						previousMonth = 11;
						nextMonth = 1;
					} else {
						previousYear = currentYear;
						previousMonth = currentMonth - 1;
						nextMonth = currentMonth + 1;
					}

					dummyDate = new Date(currentYear, currentMonth - 1, 1);
					firstDayIndex = dummyDate.getDay();
					daysThisMonth = this.getDaysInMonth(currentYear, currentMonth - 1); // NOTE: 0-indexed utility function, hence -1
					daysLastMonth = this.getDaysInMonth(previousYear, previousMonth - 1); // NOTE: 0-indexed utility function, hence -1
					range = this.Utils.range(1, daysThisMonth + 1); // generate an array, [1, 2, 3, ..., n] for n days
					days = [];

					for (var i = 0; firstDayIndex - i > 0; i++) {
						if (options.insertAdjacentValues) {
							days.unshift({
								active: false,
								month: previousMonth,
								day: daysLastMonth,
							});

							daysLastMonth--;
						} else if (options.insertNullValues) {
							days.push(null);
						}
					}

					while (range.length > 0) {
						day = range.shift();
						days.push({
							active: true,
							month: currentMonth,
							day,
						});

						if (days.length === 7) {
							result.push(days);
							days = [];
						}
					}

					if (days.length > 0) {
						day = 1; // keep track of the next month's day

						while (days.length < 7) {
							if (options.insertAdjacentValues) {
								days.push({
									active: false,
									month: nextMonth,
									day,
								});

								day++;
							} else if (options.insertNullValues) {
								days.push(null);
							}
						}

						result.push(days);
					}

					return result;
				},
			},

			/**
			 * Format timestamp using custom template string.
			 *
			 * @method Time#format
			 * @param {string} template - Format template (e.g., '%B %d, %Y')
			 * @param {number} [timestamp] - Unix timestamp (defaults to now)
			 * @param {string} [timezone] - Timezone name (defaults to preferred)
			 * @returns {string} Formatted date string
			 */
			format: {
				public: true,
				value(context, template, timestamp, timezone) {
					if (!this.isInt(timestamp)) {
						// default to today
						timestamp = this.getTime();
					}

					if (!this.isString(timezone, 1)) {
						// default to preferred timezone
						timezone = this.getPreferredTimezone();
					}

					var m = moment(timestamp).tz(timezone),
						local = moment(this.getTime()).tz(timezone),
						daysOff = m.diff(local, `days`), // how many days from now this is
						dayLabel =
							daysOff === 0 ? `Today` : daysOff === 1 ? `Tomorrow` : daysOff === -1 ? `Yesterday` : m.format(`dddd`),
						// Defines replacements for a template string
						// NOTE: If this is updated, also update replacements in LOCALE_REPLACEMENTS
						replacements = [
							{ key: `%Y`, value: m.format(`YYYY`) }, // Four-digit year
							{ key: `%y`, value: m.format(`YY`) }, // Two-digit year
							{ key: `%N`, value: m.format(`MM`) }, // Two-digit month, enforce leading zero
							{ key: `%n`, value: m.format(`M`) }, // Month number
							{ key: `%M`, value: m.format(`MMMM`) }, // Full month name
							{ key: `%m`, value: m.format(`MMM`) }, // Abbreviated month name
							{ key: `%D`, value: m.format(`DD`) }, // Date day (e.g. May *21*), enforce leading zero
							{ key: `%d`, value: m.format(`D`) }, // Date day (e.g. May *21*)
							{ key: `%L`, value: dayLabel }, // Either a relevant day label, like Today, or the day of the week
							{ key: `%W`, value: m.format(`dddd`) }, // Full weekday name
							{ key: `%w`, value: m.format(`ddd`) }, // Abbreviated weekday name
							{ key: `%J`, value: m.format(`HH`) }, // 24-hour hour (enforce leading zeroes)
							{ key: `%H`, value: m.format(`hh`) }, // two-digit hour (enforce leading zeroes)
							{ key: `%h`, value: m.format(`h`) }, // hour
							{ key: `%i`, value: m.format(`mm`) }, // two-digit minute (enforce leading zero)
							{ key: `%S`, value: m.format(`ss`) }, // second
							{ key: `%A`, value: m.format(`A`) }, // AM/PM
							{ key: `%a`, value: m.format(`a`) }, // am/pm
							{ key: `%T`, value: m.tz() }, // full timezone
							{ key: `%t`, value: m.format(`zz`) }, // short timezone
							{ key: `%Z`, value: m.format(`Z`) }, // timezone offset
						];

					// Loop through all the formatting options, replacing each special symbol with its value.
					return this.reduce(
						replacements,
						function (memo, replacement) {
							return memo.replace(replacement.key, replacement.value);
						},
						template
					);
				},
			},

			/**
			 * Uses the Intl API to format a Date/Time string from a timestamp based on a template and locale
			 * @method Time#formatLocale
			 * @param {string} formatOrTemplate - Friendly format string defined in DATE_TIME_FORMAT or template using keys defined in LOCALE_REPLACEMENTS
			 * @param {number} timestamp - Timestamp to format
			 * @param {string} [timezone] - Timezone to use
			 * @param {string} locale - Locale to use
			 * @returns {string} Formatted date string
			 */
			formatLocale: {
				public: true,
				value(_, formatOrTemplate, timestamp, timezone, locale) {
					if (!this.isInt(timestamp)) {
						// default to today
						timestamp = this.getTime();
					}

					if (!this.isString(timezone, 1)) {
						// default to preferred timezone
						timezone = this.getPreferredTimezone();
					}

					// Fallback to calling format if Intl API is not supported
					if (!SUPPORTS_LOCALE) {
						const localeFormat = DATE_TIME_FORMATS[formatOrTemplate];
						let template;

						// Check if this is a known friendly format
						if (localeFormat) {
							template = localeFormat.template;
						} else {
							template = formatOrTemplate;
						}

						const localeString = this.format(template, timestamp, timezone);

						if (formatOrTemplate === `WEEKDAY_X_SHORT`) {
							return localeString.toUpperCase().charAt(0);
						}

						return localeString;
					}

					const localeOptions = this.assign(this.getLocaleOptions(formatOrTemplate), { timeZone: timezone });

					try {
						const localeString = formatInZone(locale, localeOptions, timestamp, timezone);

						if (formatOrTemplate === `WEEKDAY_X_SHORT`) {
							return localeString.toLocaleUpperCase(locale).charAt(0);
						}

						return localeString;
					} catch (e) {
						this.log({
							type: `error`,
							message: `Time.formatLocale: Error %o`,
							args: [e],
						});
					}
				},
			},

			/**
			 * Format date/time range using Intl API (locale-aware).
			 *
			 * @method Time#formatLocaleRange
			 * @param {string} formatOrTemplate - Format name or template string
			 * @param {number} startTimestamp - Start timestamp
			 * @param {number} endTimestamp - End timestamp
			 * @param {string} [timezone] - Timezone name (defaults to preferred)
			 * @param {string} [locale] - Locale code
			 * @returns {string} Formatted date range string
			 */
			formatLocaleRange: {
				public: true,
				value(_, formatOrTemplate, startTimestamp, endTimestamp, timezone, locale) {
					if (!this.isInt(startTimestamp)) {
						this.log({
							type: `error`,
							message: `Time.formatLocaleRange: Invalid startTimestamp %o`,
							args: [startTimestamp],
						});

						throw new Error(`Time.formatLocaleRange: Invalid startTimestamp`);
					}

					if (!this.isInt(endTimestamp)) {
						this.log({
							type: `error`,
							message: `Time.formatLocaleRange: Invalid endTimestamp %o`,
							args: [endTimestamp],
						});

						throw new Error(`Time.formatLocaleRange: Invalid endTimestamp`);
					}

					if (!this.isString(timezone, 1)) {
						// default to preferred timezone
						timezone = this.getPreferredTimezone();
					}

					// Fallback to calling format if Intl API is not supported
					if (!SUPPORTS_LOCALE) {
						const localeFormat = DATE_TIME_FORMATS[formatOrTemplate];
						let template;

						// Check if this is a known friendly format
						if (localeFormat) {
							template = localeFormat.template;
						} else {
							template = formatOrTemplate;
						}

						const startTimeString = this.format(template, startTimestamp, timezone);
						const endTimeString = this.format(template, endTimestamp, timezone);

						return `${startTimeString} - ${endTimeString}`; // exit early
					}

					const localeOptions = this.getLocaleOptions(formatOrTemplate);
					localeOptions.timeZone = timezone;

					try {
						return formatInZone(locale, localeOptions, startTimestamp, timezone, endTimestamp);
					} catch (e) {
						this.log({
							type: `error`,
							message: `Time.formatLocaleRange: Error %o`,
							args: [e],
						});
					}
				},
			},

			/**
			 * Gets the locale options for a given format or template
			 * @method Time#getLocaleOptions
			 * @param {string} formatOrTemplate - Friendly format string defined in DATE_TIME_FORMAT or template using keys defined in LOCALE_REPLACEMENTS
			 * @returns {Object} Locale options
			 */
			getLocaleOptions: {
				private: true,
				value(formatOrTemplate) {
					const localeFormat = DATE_TIME_FORMATS[formatOrTemplate];
					let localeOptions;

					// Check if this is a known friendly format
					if (localeFormat) {
						if (localeFormat.localeOptions) {
							localeOptions = this.clone(localeFormat.localeOptions); // use the provided locale options
						} else {
							localeOptions = this.templateToLocaleOptions(localeFormat.template); // try to convert the template to locale options
						}
					} else {
						localeOptions = this.templateToLocaleOptions(formatOrTemplate); // not a known format, try to convert the template to locale options
					}

					return localeOptions;
				},
			},

			/**
			 * Converts a template using keys defined in LOCALE_REPLACEMENTS to locale options
			 * @method Time#templateToLocaleOptions
			 * @param {string} template - Template to convert
			 * @returns {Object} Locale options
			 */
			templateToLocaleOptions: {
				private: true,
				value(template) {
					const options = {};

					LOCALE_REPLACEMENTS.forEach((replacement) => {
						if (template.includes(replacement.key)) {
							this.assign(options, replacement.localeOptions);
						}
					});

					return options;
				},
			},

			/**
			 * Get available date/time format definitions.
			 *
			 * @method Time#getDateTimeFormats
			 * @returns {Object} Format name to template mapping
			 */
			getDateTimeFormats: {
				public: true,
				value() {
					return DATE_TIME_FORMATS;
				},
			},

			/**
			 * Calculate days until timestamp (from today).
			 *
			 * @method Time#daysUntil
			 * @param {number} timestamp - Target Unix timestamp
			 * @param {string} [timezone] - Timezone name
			 * @returns {number} Number of days (negative if past)
			 */
			daysUntil: {
				public: true,
				value(context, timestamp, timezone) {
					// calculate the number of days until the passed in timestamp
					var now = moment(this.getTime()).tz(timezone),
						target = moment(timestamp).tz(timezone);

					// reset both dates to midnight before comparing
					// ex. avoids 10pm Friday being "1 day" from a 10am event on Sunday
					this.each([`millisecond`, `second`, `minute`, `hour`], function (property) {
						target[property](0);
						now[property](0);
					});

					return target.diff(now, `days`);
				},
			},

			/**
			 * Calculate difference between two timestamps.
			 *
			 * @method Time#diff
			 * @param {number} start - Start timestamp
			 * @param {number} end - End timestamp
			 * @param {string} [units] - Unit of time ('days', 'hours', etc.)
			 * @param {string} [timezone] - Timezone name
			 * @returns {number} Difference in specified units (or milliseconds if no units)
			 */
			diff: {
				public: true,
				value(context, start, end, units, timezone) {
					var startDate = moment(start),
						endDate = moment(end);

					if (this.isString(timezone, 1)) {
						startDate = startDate.tz(timezone);
						endDate = endDate.tz(timezone);
					}

					if (this.isString(units, 1)) {
						return endDate.startOf(units).diff(startDate.startOf(units), units);
					} else {
						return endDate.diff(startDate);
					}
				},
			},

			/**
			 * Add time to timestamp.
			 *
			 * @method Time#add
			 * @param {number} timestamp - Source timestamp
			 * @param {number} value - Amount to add
			 * @param {string} units - Unit of time ('days', 'hours', etc.)
			 * @returns {number} New timestamp
			 */
			add: {
				public: true,
				value(context, timestamp, value, units) {
					return moment(timestamp).add(value, units).valueOf();
				},
			},

			/**
			 * Convert UTC time object to local timezone and format.
			 *
			 * @method Time#getUtcLocalTime
			 * @param {Object} time - Time object
			 * @param {number} time.hours - Hours (0-23)
			 * @param {number} time.minutes - Minutes (0-59)
			 * @param {string} format - Time format template
			 * @param {string} [timezone] - Target timezone (defaults to preferred)
			 * @returns {string} Formatted time string
			 */
			getUtcLocalTime: {
				public: true,
				value(context, time, format, timezone) {
					time = this.defaults(time, {
						hours: 0,
						minutes: 0,
					});

					var utcMidnight = moment().utc().startOf(`day`);

					if (!this.isString(timezone, 1)) {
						timezone = this.getPreferredTimezone();
					}

					utcMidnight.hours(time.hours).minutes(time.minutes);

					return utcMidnight.tz(timezone).format(format);
				},
			},

			/**
			 * Fetch list of available timezones from API.
			 *
			 * @method Time#getTimezoneList
			 * @param {Object} [options] - Request options
			 * @returns {Promise<Array>} Array of timezone objects with label/value
			 */
			getTimezoneList: {
				async: true,
				public: true,
				value(context, options) {
					return this.XHR.ajax({
						type: `GET`,
						url: `/api/timezones`,
						then: function (timezones) {
							return this.map(timezones, function (timezone) {
								return {
									label: timezone.id,
									value: timezone.id,
								};
							});
						},
					});
				},
			},

			/**
			 * Convert timestamp between timezones.
			 *
			 * @method Time#convert
			 * @param {string} desiredTz - Target timezone
			 * @param {number} timestamp - Unix timestamp
			 * @param {string} [currentTz] - Current timezone (defaults to local)
			 * @returns {number} Converted timestamp
			 */
			convert: {
				public: true,
				value(context, desiredTz, timestamp, currentTz) {
					if (!this.isString(currentTz, 1)) {
						currentTz = this.getTimezone();
					}

					var dateString = this.format(`%Y-%N-%DT%J:%i`, timestamp, currentTz);
					return moment.tz(dateString, desiredTz).valueOf();
				},
			},

			/**
			 * Get browser's detected timezone.
			 *
			 * @method Time.getTimezone
			 * @static
			 * @returns {string} Timezone name (e.g., 'America/New_York')
			 */
			getTimezone: {
				static: true,
				public: true,
				value() {
					return jstz.determine().name();
				},
			},

			/**
			 * Get preferred timezone (falls back to browser timezone).
			 *
			 * @method Time#getPreferredTimezone
			 * @returns {string} Timezone name
			 */
			getPreferredTimezone: {
				public: true,
				value(context) {
					if (!this.isString(PREFERRED_TIMEZONE, 1)) {
						return this.getTimezone();
					} else {
						return PREFERRED_TIMEZONE;
					}
				},
			},

			/**
			 * Get UTC midnight timestamp for local date.
			 *
			 * @method Time#getUtcMidnight
			 * @param {number} timestamp - Source timestamp
			 * @param {string} timezone - Local timezone
			 * @returns {number} UTC timestamp for midnight of that date
			 */
			getUtcMidnight: {
				public: true,
				private: false,
				value(context, timestamp, timezone) {
					var localDate = moment(timestamp).tz(timezone), // construct a momentjs object with the passed params
						utcDate = moment(localDate.format(`YYYY M D`), `YYYY M D`).tz(`UTC`); // create a UTC date, based only on the date info

					return utcDate.hours(0).seconds(0).milliseconds(0).valueOf(); // ensure it's set for midnight, and return
				},
			},

			/**
			 * Set preferred timezone for future operations.
			 *
			 * @method Time.setPreferredTimezone
			 * @static
			 * @param {string} timezone - Timezone name
			 */
			setPreferredTimezone: {
				static: true,
				public: true,
				value(timezone) {
					PREFERRED_TIMEZONE = timezone;
				},
			},

			/**
			 * Get timestamp at start of time unit.
			 *
			 * @method Time#startOf
			 * @param {number} timestamp - Source timestamp
			 * @param {string} unit - Unit of time ('day', 'month', 'year', etc.)
			 * @param {string} [timezone] - Timezone name
			 * @returns {number} New timestamp at start of unit
			 * @see {@link https://momentjs.com/docs/#/manipulating/start-of/|Moment.js startOf}
			 */
			startOf: {
				public: true,
				value(context, timestamp, unit, timezone) {
					var m = moment(timestamp);

					if (this.isString(timezone, 1)) {
						m.tz(timezone);
					}

					return m.startOf(unit).valueOf();
				},
			},

			/**
			 * Extract unit values from timestamp.
			 *
			 * @method Time#getByUnits
			 * @param {number} timestamp - Source timestamp
			 * @param {string[]} units - Units to extract (e.g., ['year', 'month', 'date'])
			 * @param {string} [timezone] - Timezone name
			 * @returns {Object} Object with unit values (e.g., { year: 2024, month: 0 })
			 */
			getByUnits: {
				public: true,
				value(context, timestamp, units, timezone) {
					var m = moment(timestamp),
						value = {};

					if (this.isString(timezone, 1)) {
						m.tz(timezone);
					}

					this.each(units, function (unit) {
						value[unit] = m.get(unit);
					});

					return value;
				},
			},

			/**
			 * Set unit values on timestamp.
			 *
			 * @method Time#setByUnits
			 * @param {number} timestamp - Source timestamp
			 * @param {Object} values - Unit values to set (e.g., { date: 1, hour: 12 })
			 * @param {string} [timezone] - Timezone name
			 * @returns {number} New timestamp with updated values
			 */
			setByUnits: {
				public: true,
				value(context, timestamp, values, timezone) {
					var m = moment(timestamp);

					if (this.isString(timezone, 1)) {
						m.tz(timezone);
					}

					this.each(values, function (value, unit) {
						m.set(unit, value);
					});

					return m.valueOf();
				},
			},

			/**
			 * Get reference timestamp for weekday (for locale formatting).
			 *
			 * NOTE: Must format with UTC timezone to get correct day.
			 *
			 * @method Time#getWeekdayTimestamp
			 * @param {string} weekday - Weekday name ('SUNDAY', 'MONDAY', etc.)
			 * @returns {number} Reference timestamp for that weekday
			 */
			getWeekdayTimestamp: {
				public: true,
				value(_, weekday) {
					const sundayTimestamp = 1704585600000; // Known Sunday: January 7, 2024 at midnight UTC
					const msPerDay = 86400000;
					const WEEKDAY_OFFSET = {
						SUNDAY: 0,
						MONDAY: 1,
						TUESDAY: 2,
						WEDNESDAY: 3,
						THURSDAY: 4,
						FRIDAY: 5,
						SATURDAY: 6,
					};
					const offset = WEEKDAY_OFFSET[weekday.toUpperCase()];

					if (offset === undefined) {
						throw new Error(`Time.getWeekdayTimestamp: Invalid weekday ${weekday}`);
					}

					return sundayTimestamp + offset * msPerDay;
				},
			},

			/**
			 * Check if date range spans multiple days.
			 *
			 * @method Time#isDateRangeMultiDay
			 * @param {Object} options - Options
			 * @param {number} options.startDate - Start timestamp
			 * @param {number} options.endDate - End timestamp
			 * @returns {boolean} True if duration > 24 hours
			 */
			isDateRangeMultiDay: {
				public: true,
				value(_, options) {
					const { startDate, endDate } = options;

					const eventDuration = endDate - startDate;
					return eventDuration > 86400000; // 1000 ms * 60 sec * 60 min * 24 hour\
				},
			},
		},
	});
});

//# sourceMappingURL=Time.js.map